import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import BookingForm from "../components/BookingForm";
import PaymentModal from "../components/PaymentModal";
import TutorAvatar from "../components/TutorAvatar";
import { formatCurrency } from "../lib/format";
import { getTutors } from "../lib/storage";

export default function TutorProfile() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [tutor, setTutor] = useState(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const bookingRef = useRef(null);

  useEffect(() => {
    const match = getTutors().find((item) => item.id === id);
    setTutor(match || null);
  }, [id]);

  useEffect(() => {
    if (params.get("book") === "1" && bookingRef.current) {
      bookingRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [params, tutor]);

  const reviewAverage = useMemo(() => {
    if (!tutor?.reviews?.length) {
      return tutor?.rating || 0;
    }
    return tutor.reviews.reduce((total, review) => total + review.rating, 0) / tutor.reviews.length;
  }, [tutor]);

  if (!tutor) {
    return (
      <section className="section-shell pt-16">
        <div className="surface-panel p-10 text-center">
          <h1 className="font-display text-4xl font-bold text-ink">Tutor not found</h1>
          <p className="mt-4 text-slate-600">The tutor profile could not be loaded.</p>
          <Link to="/marketplace" className="button-primary mt-6">
            Back to marketplace
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="section-shell pt-10">
      <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
        <article className="surface-panel relative overflow-hidden p-8 lg:p-10">
          <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-signal via-sun to-skyglow" />
          <div className="relative z-10">
            <div className="flex flex-col gap-6 lg:flex-row">
              <TutorAvatar
                src={tutor.image}
                name={tutor.name}
                alt={`${tutor.name} profile image`}
                className="h-32 w-32 rounded-[32px]"
                textClassName="text-4xl"
              />
              <div className="flex-1">
                <div className="eyebrow-light">Verified Tutor</div>
                <h1 className="mt-4 font-display text-4xl font-extrabold text-ink">{tutor.name}</h1>
                <p className="mt-2 text-lg font-semibold text-slate-600">{tutor.headline}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="chip">Star {reviewAverage.toFixed(1)}</span>
                  <span className="chip">{formatCurrency(tutor.price)}/hr</span>
                  <span className="chip">{tutor.qualifications}</span>
                </div>
              </div>
            </div>

            <div className="mt-8 grid gap-4 md:grid-cols-3">
              <div className="metric-tile">
                <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-slate-400">Experience</p>
                <p className="mt-2 font-display text-3xl font-extrabold text-ink">{tutor.experienceYears}+ yrs</p>
              </div>
              <div className="metric-tile">
                <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-slate-400">Category</p>
                <p className="mt-2 font-display text-2xl font-extrabold text-ink">{tutor.category}</p>
              </div>
              <div className="metric-tile">
                <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-slate-400">Subjects</p>
                <p className="mt-2 text-sm font-bold leading-6 text-slate-700">{tutor.subjects.join(", ")}</p>
              </div>
            </div>

            <p className="mt-8 text-base leading-8 text-slate-600">{tutor.fullBio}</p>

            <div className="mt-8 flex flex-wrap gap-2">
              {(tutor.tags || tutor.subjects).map((tag) => (
                <span key={tag} className="chip">
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={`/tutor/${tutor.id}?book=1`} className="button-primary">
                Book a Tutor
              </Link>
              <Link to="/marketplace" className="button-secondary">
                Back to Marketplace
              </Link>
            </div>
          </div>
        </article>

        <aside className="dark-shell p-6 lg:p-7">
          <div className="space-y-6">
            <div>
              <div className="eyebrow">Availability Slots</div>
              <ul className="mt-5 space-y-3">
                {(tutor.availabilitySlots || []).map((slot) => (
                  <li
                    key={slot}
                    className="rounded-2xl border border-white/10 bg-white/8 px-4 py-3 text-sm font-semibold text-white/82"
                  >
                    {slot}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="eyebrow">Reviews</div>
              <div className="mt-5 space-y-4">
                {(tutor.reviews || []).map((review) => (
                  <article key={`${review.name}-${review.quote}`} className="glow-card p-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-semibold text-white">{review.name}</p>
                      <p className="text-sm font-bold text-skyglow">Star {review.rating}</p>
                    </div>
                    <p className="mt-3 text-sm leading-7 text-white/74">{review.quote}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </aside>
      </div>

      <div ref={bookingRef} className="mt-12 grid gap-8 lg:grid-cols-[0.95fr_1.05fr]">
        <div className="surface-panel p-8">
          <div className="eyebrow-light">Booking Panel</div>
          <h2 className="section-title mt-4">Book a demo with {tutor.name}</h2>
          <p className="mt-4 text-sm leading-7 text-slate-600">
            Every booking creates a lead and booking record in localStorage, opens WhatsApp with a
            prefilled message, and stays available for internal follow-up.
          </p>
          <div className="mt-6 grid gap-3">
            <div className="soft-panel px-4 py-4 text-sm font-semibold text-slate-700">
              Direct tutor profile URLs work on Hostinger when the included .htaccess file is
              uploaded.
            </div>
            <div className="soft-panel px-4 py-4 text-sm font-semibold text-slate-700">
              The Pay button below explains how to swap the placeholder flow for Cashfree.
            </div>
          </div>
        </div>

        <BookingForm
          tutor={tutor}
          submitLabel="Book Demo on WhatsApp"
          contextLabel={`tutor-profile:${tutor.id}`}
          showPayment
          onPay={() => setPaymentOpen(true)}
        />
      </div>

      <PaymentModal open={paymentOpen} onClose={() => setPaymentOpen(false)} tutorName={tutor.name} />
    </section>
  );
}
